import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {useGetLiveEventContext} from "../context/get-live-event";
import {OfferingFormCheckoutFormValues} from "../../../../components/offering-form/general/checkout-form/types";
import {offeringCheckoutFormValidationSchema} from "../../../../components/offering-form/general/checkout-form/validation-schema";
import {useEditLiveEventHook} from "../hooks/edit-live-event";
import {useNavigate} from "react-router-dom";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {EditLiveEventFormValues} from "../types";
import {FormWrapper} from "../../../../components/form-wrapper";
import {LiveEventCheckoutFormFields} from "../../../../components/offering-form/live-events/checkout-form";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {toSavedEditLiveEventFormValues} from "../saved-form-values";

export function EditLiveEventCheckoutForm() {
  const navigate = useNavigate();
  const {
    state: {liveEventData},
  } = useGetLiveEventContext();
  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: liveEventData.bookingQuestions
        ? liveEventData.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
              };
            }
          })
        : [],
    },
  });
  const {editLiveEvent, isEditingLiveEvent} = useEditLiveEventHook();

  function onSubmit(values: OfferingFormCheckoutFormValues) {
    // The update carries the pricing, redirect and location with it, so everything this
    // tab does not edit goes back exactly as saved.
    const payload: EditLiveEventFormValues = {
      ...toSavedEditLiveEventFormValues(liveEventData, IS_PWYW_ENABLED),
      id: liveEventData.id,
      cover_images: liveEventData.cover_images ?? [],
      bookingQuestions: values.bookingQuestions,
    };
    editLiveEvent(payload);
  }

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  return (
    <FormWrapper>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <LiveEventCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => navigate("/live-classes")}
                    block
                    size="small"
                    type="default"
                    className="secondary-btn"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                    loading={isEditingLiveEvent}
                  >
                    Update live event
                  </Button>
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </FormWrapper>
  );
}
